import { useRef, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Row, Col } from 'reactstrap';
import { useSnackbar } from 'notistack';

import useAuth from 'src/hooks/useAuth';
import formatDateFns from 'src/utils/formatDateFns';
import Dialog from 'src/componentes/dialog';
import DatePicker from 'src/componentes/date-picker';
import PeriodicidadeRecorrenciaLookup from 'src/lookups/Recorrencia/PeriodicidadeRecorrenciaLookup';
import Button from 'src/componentes/button';
import InputNumber from 'src/componentes/input-number';
import { capitalizeFirstLetter } from 'src/utils/string';

const ORDINAL_KEYS = ['primeira', 'segunda', 'terceira', 'quarta', 'ultima'];

const EditRecorrencia = ({
  recorrencia,
  handleChangeRecorrencia,
  isDisabled,
  isEdit,
  ehMaisDeUmDia,
  ...rest
}) => {
  const intl = useIntl();
  const ref = useRef();
  const { user } = useAuth();
  const { currentLocale } = formatDateFns(user?.idioma);
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState({});
  const [occurrenceText, setOccurrenceText] = useState('');

  // Calcula informações sobre o dia selecionado
  const getMonthlyInfo = (date) => {
    if (!date) return null;

    const d = new Date(date);
    const dayOfMonth = d.getDate();
    const weekday = d.getDay(); // 0 (dom) .. 6 (sab)
    const occurrence = Math.ceil(dayOfMonth / 7); // 1..5
    const year = d.getFullYear();
    const month = d.getMonth(); // 0-11

    // Conta quantas vezes esse weekday aparece no mês
    const lastDay = new Date(year, month + 1, 0).getDate();
    let countWeekday = 0;
    for (let dd = 1; dd <= lastDay; dd++) {
      if (new Date(year, month, dd).getDay() === weekday) {
        countWeekday++;
      }
    }

    const weekdayName =
      currentLocale?.localize?.day?.(weekday, { width: 'wide' }) ||
      new Date(1970, 0, 4 + weekday).toLocaleDateString(undefined, {
        weekday: 'long',
      });

    const monthName =
      currentLocale?.localize?.month?.(month, { width: 'wide' }) ||
      d.toLocaleDateString(undefined, { month: 'long' });

    return {
      dayOfMonth,
      weekday,
      occurrence,
      countWeekday,
      weekdayName,
      monthName,
      monthNumber: month + 1, // 1-12
    };
  };

  const handleChangeForm = (field, value) => {
    if (
      field === 'periodicidadeRecorrencia' &&
      value &&
      (value.id === 1 || value.id === 2) &&
      ehMaisDeUmDia
    ) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'msgErroPeriodicidadeRecorrenciaMaisDeUmDia',
        }),
        {
          variant: 'error',
        }
      );
      return;
    }

    // Se estiver alterando a data inicial e for criação,
    // mantém a primeira opção selecionada (repetirNoDia) e atualiza noDia / mesDoDia.
    if (field === 'dataInicioRecorrencia' && !isEdit) {
      const info = getMonthlyInfo(value);
      setForm((prev) => ({
        ...prev,
        dataInicioRecorrencia: value,
        repetirNoDia: true,
        repetirNa: false,
        isUltimaSemana: false,
        noDia: info?.dayOfMonth,
        ...(info
          ? {
              mesDoDia: info.monthNumber ? { id: info.monthNumber } : undefined,
            }
          : {}),
        diaDaSemana: undefined,
        semana: undefined,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Inicialização do formulário
  useEffect(() => {
    if (isEdit) {
      setForm(recorrencia);
    } else {
      const dataAtual = new Date();
      const dataTermino = new Date(dataAtual.getTime());
      dataTermino.setDate(dataTermino.getDate() + 182);
      const infoAtual = getMonthlyInfo(dataAtual);

      setForm({
        dataInicioRecorrencia: dataAtual,
        contadorRecorrencia: 1,
        periodicidadeRecorrencia: ehMaisDeUmDia ? { id: 3 } : { id: 2 },
        diasRecorrencia: [infoAtual?.weekday ?? 2],
        dataTerminoRecorrencia: dataTermino,
        repetirNoDia: true,
        repetirNa: false,
        isUltimaSemana: false,
        noDia: infoAtual?.dayOfMonth,
        mesDoDia: infoAtual?.monthNumber
          ? { id: infoAtual.monthNumber }
          : undefined,
      });
    }
  }, [isEdit, recorrencia]);

  // Handlers para seleção mensal
  const handleSelectMensalNoDia = (info) => {
    setForm((prev) => ({
      ...prev,
      repetirNoDia: true,
      repetirNa: false,
      isUltimaSemana: false,
      noDia: info.dayOfMonth,
      diaDaSemana: undefined,
      semana: undefined,
    }));
  };

  const handleSelectMensalNa = (info, isUltima = false) => {
    setForm((prev) => ({
      ...prev,
      repetirNoDia: false,
      repetirNa: true,
      isUltimaSemana: isUltima,
      noDia: undefined,
      diaDaSemana: info.weekday != null ? { id: info.weekday } : undefined,
      semana: { id: isUltima ? 5 : info.occurrence },
    }));
  };

  // Handlers para seleção anual
  const handleSelectAnualNoDia = (info) => {
    setForm((prev) => ({
      ...prev,
      repetirNoDia: true,
      repetirNa: false,
      isUltimaSemana: false,
      noDia: info.dayOfMonth,
      mesDoDia: info.monthNumber != null ? { id: info.monthNumber } : undefined,
      diaDaSemana: undefined,
      semana: undefined,
      mesDoAno: undefined,
    }));
  };

  const handleSelectAnualNa = (info, isUltima = false) => {
    setForm((prev) => ({
      ...prev,
      repetirNoDia: false,
      repetirNa: true,
      isUltimaSemana: isUltima,
      noDia: undefined,
      mesDoDia: undefined,
      diaDaSemana: info.weekday != null ? { id: info.weekday } : undefined,
      semana: { id: isUltima ? 5 : info.occurrence },
      mesDoAno: info.monthNumber != null ? { id: info.monthNumber } : undefined,
    }));
  };

  // Handler para mudança de dias na periodicidade semanal
  const handleChangeDias = (id) => {
    const dias = [...(form?.diasRecorrencia || [])];
    const indexDia = dias.indexOf(id);

    if (indexDia !== -1) {
      // Se for o único dia selecionado, não permite remover
      if (dias.length === 1) {
        return;
      }
      dias.splice(indexDia, 1);
    } else {
      dias.push(id);
    }

    // Se todos os 7 dias foram selecionados, converte para diária
    if (dias.length === 7) {
      setForm((prev) => ({
        ...prev,
        periodicidadeRecorrencia: { id: 1 }, // diária
        diasRecorrencia: [],
      }));
      return;
    }

    handleChangeForm('diasRecorrencia', dias);
  };

  // Assegura que, ao trocar para periodicidade semanal, haja ao menos um dia selecionado
  useEffect(() => {
    if (form?.periodicidadeRecorrencia?.id === 2) {
      if (!form?.diasRecorrencia || form.diasRecorrencia.length === 0) {
        const info = getMonthlyInfo(form?.dataInicioRecorrencia);
        const defaultWeekday = info?.weekday ?? new Date().getDay() ?? 2;
        setForm((prev) => ({ ...prev, diasRecorrencia: [defaultWeekday] }));
        return;
      }
    }
  }, [form?.periodicidadeRecorrencia?.id]);

  const computeOccurrenceText = (f) => {
    const info = getMonthlyInfo(f?.dataInicioRecorrencia);
    const periodicidadeId = f?.periodicidadeRecorrencia?.id;
    const repeteMaisQueUm = f?.contadorRecorrencia > 1;

    if (!periodicidadeId || !info) return '';

    // Diário
    if (periodicidadeId === 1) {
      return intl.formatMessage(
        { id: 'label.OcorreDiariamente' },
        { ocorrencias: f?.contadorRecorrencia }
      );
    }

    // Semanal
    if (periodicidadeId === 2) {
      const dias = form?.diasRecorrencia || [];
      if (!dias || dias.length === 0) return '';
      const sorted = [...dias].sort((a, b) => a - b);
      const daysNames = sorted
        .map((idx) => {
          const name =
            currentLocale?.localize?.day?.(idx, { width: 'wide' }) ||
            new Date(1970, 0, 4 + idx).toLocaleDateString(undefined, {
              weekday: 'long',
            });
          return capitalizeFirstLetter(name);
        })
        .join(', ');

      if (repeteMaisQueUm) {
        return intl.formatMessage(
          { id: 'label.OcorreNoDiaDaSemanaComContador' },
          { diasDaSemana: daysNames, ocorrencias: f?.contadorRecorrencia }
        );
      } else {
        return intl.formatMessage(
          { id: 'label.OcorreNoDiaDaSemana' },
          { diasDaSemana: daysNames }
        );
      }
    }

    // Mensal
    if (periodicidadeId === 3) {
      if (f?.repetirNoDia) {
        return intl.formatMessage(
          { id: 'label.OcorreNoDiaTodoMes' },
          { diaDoMes: info.dayOfMonth, ocorrencias: f?.contadorRecorrencia }
        );
      }
      if (f?.repetirNa) {
        const semanaId = f?.semana?.id;
        const ordinalKey = ORDINAL_KEYS[semanaId - 1] || ORDINAL_KEYS[0];
        const ordinal = intl.formatMessage({ id: ordinalKey });
        return intl.formatMessage(
          { id: 'label.ocorreNaSemanaNoMes' },
          {
            semana: ordinal,
            nomeDiaDaSemana: info.weekdayName,
            ocorrencias: f?.contadorRecorrencia,
          }
        );
      }
    }

    // Anual
    if (periodicidadeId === 4) {
      if (f?.repetirNoDia) {
        return intl.formatMessage(
          { id: 'label.ocorreNoDiaNoMes' },
          {
            diaDoMes: info.dayOfMonth,
            nomeMes: capitalizeFirstLetter(info.monthName),
          }
        );
      }
      if (f?.repetirNa) {
        const semanaId = f?.semana?.id;
        const ordinalKey = ORDINAL_KEYS[semanaId - 1] || ORDINAL_KEYS[0];
        const ordinal = intl.formatMessage({ id: ordinalKey });
        return intl.formatMessage(
          { id: 'label.ocorreNaSemanaDoMes' },
          {
            semana: ordinal,
            nomeDiaDaSemana: info.weekdayName,
            nomeMes: capitalizeFirstLetter(info.monthName),
          }
        );
      }
    }

    return '';
  };

  // Atualiza occurrenceText sempre que o form muda
  useEffect(() => {
    setOccurrenceText(computeOccurrenceText(form));
  }, [form]);

  // Renderiza os dias da semana para periodicidade semanal
  const renderDaysOfWeek = () => (
    <div className="d-flex mb-3">
      {Array(7)
        .fill(0)
        .map((_, index) => {
          const weekDay = currentLocale?.localize?.day?.(index, {
            width: 'abbreviated',
          });
          const isActive = form?.diasRecorrencia?.includes(index);

          return (
            <div
              key={index}
              onClick={() => !isDisabled && handleChangeDias(index)}
              className={`d-flex align-items-center justify-content-center cursor-pointer rounded-circle mr-2 ${
                isActive ? 'bg-primary text-white' : ''
              }`}
              style={{ height: 30, width: 30 }}
            >
              {weekDay && capitalizeFirstLetter(weekDay[0])}
            </div>
          );
        })}
    </div>
  );

  // Renderiza opções para periodicidade mensal
  const renderMensal = () => {
    const info = getMonthlyInfo(form?.dataInicioRecorrencia);
    if (!info) return null;

    let options;
    const ordinalKey = ORDINAL_KEYS[info.occurrence - 1] || ORDINAL_KEYS[0];
    const ordinal = intl.formatMessage({ id: ordinalKey });
    options = [
      {
        id: 'no',
        label: intl.formatMessage(
          { id: 'label.noDia' },
          { diaDoMes: info.dayOfMonth }
        ),
        handler: () => handleSelectMensalNoDia(info),
      },
      {
        id: 'na',
        label: intl.formatMessage(
          { id: 'label.naSemanaDaqueleMes' },
          { semana: ordinal, nomeDiaDaSemana: info.weekdayName }
        ),
        handler: () => handleSelectMensalNa(info, false),
      },
    ];

    // Mantém a opção "última" quando o mês tem 5 ocorrências e a data é a 4ª
    const showUltima = info.countWeekday === 5 && info.occurrence === 4;
    const ultima = intl.formatMessage({ id: ORDINAL_KEYS[4] });
    if (showUltima) {
      options.push({
        id: 'ultima',
        label: intl.formatMessage(
          { id: 'label.naSemanaDaqueleMes' },
          { semana: ultima, nomeDiaDaSemana: info.weekdayName }
        ),
        handler: () => handleSelectMensalNa(info, true),
      });
    }

    const getSelection = () => {
      if (form?.repetirNoDia) return 'no';
      if (form?.repetirNa) return form?.isUltimaSemana ? 'ultima' : 'na';
      return null;
    };

    return (
      <Row>
        <Col md={12}>
          <div className="d-flex flex-column mb-3">
            {options.map((opt) => (
              <label
                key={opt.id}
                className="d-flex align-items-center mb-2 cursor-pointer"
                style={{ userSelect: 'none' }}
              >
                <input
                  type="radio"
                  name="mensal-options"
                  value={opt.id}
                  disabled={isDisabled}
                  checked={getSelection() === opt.id}
                  onChange={opt.handler}
                  className="mr-2"
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </Col>
      </Row>
    );
  };

  // Renderiza opções para periodicidade anual
  const renderAnual = () => {
    const info = getMonthlyInfo(form?.dataInicioRecorrencia);
    if (!info) return null;
    let options;
    const monthLabel = capitalizeFirstLetter(info.monthName);
    const ordinalKey = ORDINAL_KEYS[info.occurrence - 1] || ORDINAL_KEYS[0];
    const ordinal = intl.formatMessage({ id: ordinalKey });
    options = [
      {
        id: 'no',
        label: intl.formatMessage(
          { id: 'label.noDiaDoMes' },
          { diaDoMes: info.dayOfMonth, nomeMes: monthLabel }
        ),
        handler: () => handleSelectAnualNoDia(info),
      },
      {
        id: 'na',
        label: intl.formatMessage(
          { id: 'label.NaSemanaDoMes' },
          {
            semana: ordinal,
            nomeDiaDaSemana: info.weekdayName,
            nomeMes: monthLabel,
          }
        ),
        handler: () => handleSelectAnualNa(info, false),
      },
    ];

    const showUltima = info.countWeekday === 5 && info.occurrence === 4;
    const ultima = intl.formatMessage({ id: ORDINAL_KEYS[4] });
    if (showUltima) {
      options.push({
        id: 'ultima',
        label: intl.formatMessage(
          { id: 'label.NaSemanaDoMes' },
          {
            semana: ultima,
            nomeDiaDaSemana: info.weekdayName,
            nomeMes: monthLabel,
          }
        ),
        handler: () => handleSelectAnualNa(info, true),
      });
    }

    const getSelection = () => {
      if (form?.repetirNoDia) return 'no';
      if (form?.repetirNa) return form?.isUltimaSemana ? 'ultima' : 'na';
      return null;
    };

    return (
      <Row>
        <Col md={12}>
          <div className="d-flex flex-column mb-3">
            {options.map((opt) => (
              <label
                key={opt.id}
                className="d-flex align-items-center mb-2 cursor-pointer"
                style={{ userSelect: 'none' }}
              >
                <input
                  type="radio"
                  name="anual-options"
                  value={opt.id}
                  disabled={isDisabled}
                  checked={getSelection() === opt.id}
                  onChange={opt.handler}
                  className="mr-2"
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </Col>
      </Row>
    );
  };

  const handleClose = () => {
    if (ref.current) ref.current.close();
  };

  const handleSave = () => {
    setForm((prev) => {
      const next = { ...prev, occurrenceText };
      handleChangeRecorrencia(next);
      return next;
    });

    handleClose(true);
  };

  const sendIsDisabled =
    !form?.dataInicioRecorrencia ||
    !form?.contadorRecorrencia ||
    !form?.periodicidadeRecorrencia ||
    !form?.dataTerminoRecorrencia;

  const actions = [
    <Button
      key="cancel"
      onClick={handleClose}
      color="primary"
      outline
      className="mr-2"
    >
      {intl.formatMessage({ id: 'label.cancelar' })}
    </Button>,
    <Button
      key="save"
      disabled={sendIsDisabled || isDisabled}
      onClick={handleSave}
      color="primary"
    >
      {intl.formatMessage({ id: 'concluir' })}
    </Button>,
  ];

  return (
    <Dialog
      onRequestClose={handleClose}
      ref={ref}
      padContent
      actions={actions}
      width={400}
      height={500}
      title={intl.formatMessage({ id: 'recorrencia' })}
      {...rest}
    >
      <div className="mb-2">{intl.formatMessage({ id: 'inicio' })} *</div>
      <div className="mb-1 text-muted">
        {intl.formatMessage({ id: 'label.SelecioneDataInicioRecorrencia' })}
      </div>
      <Row>
        <Col md={12}>
          <DatePicker
            autoFormatDate
            disabled={isDisabled}
            required
            model={{
              value: form?.dataInicioRecorrencia,
              requestChange: (v) =>
                handleChangeForm('dataInicioRecorrencia', v),
            }}
          />
        </Col>
      </Row>
      <div className="mb-2">{intl.formatMessage({ id: 'repetirACada' })}</div>
      <Row>
        {form?.periodicidadeRecorrencia?.id !== 4 && (
          <Col md={5}>
            <InputNumber
              disabled={isDisabled}
              required
              min={1}
              max={99}
              style={{ height: '38px' }}
              model={{
                label: '',
                value: form?.contadorRecorrencia,
                requestChange: (v) => {
                  if (v < 1 || v > 99) {
                    enqueueSnackbar(
                      intl.formatMessage({
                        id: 'label.numeroInvalido',
                      }),
                      { variant: 'error' }
                    );
                    return;
                  }
                  handleChangeForm('contadorRecorrencia', v);
                },
              }}
            />
          </Col>
        )}
        <Col md={5}>
          <PeriodicidadeRecorrenciaLookup
            disabled={isDisabled}
            required
            model={{
              label: '',
              value: form?.periodicidadeRecorrencia,
              requestChange: (v) =>
                handleChangeForm('periodicidadeRecorrencia', v),
            }}
          />
        </Col>
      </Row>
      {form?.periodicidadeRecorrencia?.id === 2 && renderDaysOfWeek()}
      {form?.periodicidadeRecorrencia?.id === 3 && renderMensal()}
      {form?.periodicidadeRecorrencia?.id === 4 && renderAnual()}
      {occurrenceText && (
        <Row>
          <Col md={12}>
            <div className="text-muted mb-3">{occurrenceText}</div>
          </Col>
        </Row>
      )}
      <Row>
        <Col md={5}>
          <DatePicker
            autoFormatDate
            disabled={isDisabled}
            required
            model={{
              label: intl.formatMessage({ id: 'encerrar' }),
              value: form?.dataTerminoRecorrencia,
              requestChange: (v) =>
                handleChangeForm('dataTerminoRecorrencia', v),
            }}
          />
        </Col>
      </Row>
    </Dialog>
  );
};

export default EditRecorrencia;
